import React, { useMemo } from 'react'
import { useIntl } from 'react-intl'

import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import StickyPromptCard from 'components/Common/StickyPromptCard/StickyPromptCard'
import CaptionTextBlock from 'LuxKit/TextBlocks/CaptionTextBlock'
import BodyText from 'LuxKit/Typography/BodyText'
import noop from 'lib/function/noop'
import type { CarHireReengagementView } from 'components/Common/StickyPromptCard/useCarHireReengagementSelectionLogic'
import { setCarHireReengagementDismissedInStorage } from 'storage/carHireReengagement'
import qs from 'qs'
import StickyPromptCardContent from 'components/Common/StickyPromptCard/StickyPromptCardContent'
import StickyPromptCardFooter from 'components/Common/StickyPromptCard/StickyPromptCardFooter'
import PriceRowPriceCaption from 'LuxKit/PricePoints/PriceRowPriceCaption'
import PriceRowPrice from 'LuxKit/PricePoints/PriceRowPrice'
import TextButton from 'LuxKit/Button/TextButton'

interface Props {
  onClose?: () => void
  show: boolean
  stickyTop?: number
  carHireReengagementView: CarHireReengagementView
  offerSummary: App.CarHireOfferSummary
}

function CarHireReengagementCard(props: Props) {
  const {
    carHireReengagementView,
    offerSummary,
    show,
    stickyTop,
    onClose = noop,
  } = props
  const intl = useIntl()
  const onClick = () => {
    setCarHireReengagementDismissedInStorage('clicked')
    onClose()
  }

  const onDismiss = () => {
    setCarHireReengagementDismissedInStorage('closed')
    onClose()
  }

  const vehicleImage = useMemo(() => ({
    ...offerSummary.vehicle.image,
    // modify the image URL to utilise car trawlers image options for a better image
    // 'secondary' faces left, 'primary' faces right
    url: `${offerSummary.vehicle.image.url?.replace('primary', 'secondary')}?w=68&dpr=2`,
  }), [offerSummary])

  const queryParams = {
    pickUpName: carHireReengagementView.placeName,
    dropOffName: carHireReengagementView.placeName,
    pickUpId: carHireReengagementView.filters.pickUpLocationId,
    dropOffId: carHireReengagementView.filters.dropOffLocationId,
    pickUpDate: carHireReengagementView.filters.pickUpDate,
    pickUpTime: carHireReengagementView.filters.pickUpTime,
    dropOffDate: carHireReengagementView.filters.dropOffDate,
    dropOffTime: carHireReengagementView.filters.dropOffTime,
    ageCategory: carHireReengagementView.filters.driversAgeCategory,
    age: carHireReengagementView.filters.driversAge,
    pickUpSearchType: carHireReengagementView.filters.pickUpSearchType,
    dropOffSearchType: carHireReengagementView.filters.dropOffSearchType,
    isPickUpAirport: carHireReengagementView.filters.isPickUpAtAirport,
  }

  const title = intl.formatMessage(
    {
      id: 'wpp9f3aI6u',
      defaultMessage: 'Need to hire a car in {placeName}?',
      description: 'Title for car hire reengagement prompt card',
    },
    { placeName: carHireReengagementView.placeName },
  )

  return (
    <StickyPromptCard
      title={title}
      onClose={onDismiss}
      to={`/search/car-hire?${qs.stringify(queryParams)}`}
      onClick={onClick}
      show={show}
      stickyTop={stickyTop}
    >
      <StickyPromptCardContent image={vehicleImage}>
        <VerticalSpacer gap={4}>
          <BodyText weight="bold" variant="medium" lineClamp={2}>
            {intl.formatMessage(
              {
                id: 'cYTdV1CpPA',
                defaultMessage: 'We’ve found {offerCount, plural, one {# car} other {# cars}}',
                description: 'Text showing number of car hire offers found in reengagement card',
              },
              { offerCount: carHireReengagementView.offerCount },
            )}
          </BodyText>
          <CaptionTextBlock variant="medium">
            {carHireReengagementView.pickUpLocationName} - {carHireReengagementView.dropOffLocationName}
          </CaptionTextBlock>
        </VerticalSpacer>
      </StickyPromptCardContent>
      <StickyPromptCardFooter
        callToAction={<TextButton
          variant="default"
          kind="primary"
          size="small"
          nonInteractable
        >
          {intl.formatMessage({
            id: 'cLiGlnBPjy',
            defaultMessage: 'View Cars',
            description: 'Button text to view car hire search results in reengagement card',
          })}
        </TextButton>}
      >
        <PriceRowPriceCaption>
          {intl.formatMessage(
            {
              id: 'nypPPoKyzq',
              defaultMessage: '{duration, plural, one {# day} other {# days}} from',
              description: 'Duration prefix before car hire price in reengagement card',
            },
            { duration: offerSummary.duration },
          )}
        </PriceRowPriceCaption>
        <PriceRowPrice
          size="S"
          price={offerSummary.totalCharge.price}
          saleUnit="total"
        />
      </StickyPromptCardFooter>
    </StickyPromptCard>
  )
}

export default CarHireReengagementCard
